/**
 * PROPRIETARY AND CONFIDENTIAL
 * Copyright © 2026 Contractor Co-Pilot. All Rights Reserved.
 *
 * Token Utils - Main orchestrator for Clerk ↔ Supabase Token Helpers
 *
 * Refactored into modular files:
 * - token-types.ts - Type definitions
 * - token-constants.ts - Configuration constants
 * - token-state.ts - Loading state management
 * - token-cache.ts - Cache management
 * - token-queue.ts - Request queue management
 * - token-fetch.ts - Token fetching with retry
 */

// ============================================================================
// Clerk ↔ Supabase Token Helpers
// ============================================================================
// Natural Clerk-Supabase Integration: Use Clerk's default JWT
// RLS functions will extract org_id directly (no UUID casting needed)
// ============================================================================

import { metricsCollector } from "@/lib/monitoring/metrics";
import { captureMessage } from "@/lib/monitoring/sentry";
import { logger } from "@/lib/utils/logger";
import {
  clearTokenCache,
  getCacheKey,
  inFlightRequests,
  stopTokenCacheCleanup,
  tokenCache,
} from "./token-cache";
import {
  FALLBACK_TOKEN_CACHE_TTL_MS,
  PREFETCH_THRESHOLD_MS,
  STALE_TOKEN_GRACE_PERIOD_MS,
} from "./token-constants";
import { decodeTokenExpiry, fetchTokenWithRetry } from "./token-fetch";
import {
  activeTokenRequests,
  decrementActiveRequests,
  enqueueRequest,
  incrementActiveRequests,
  isAtCapacity,
  processTokenQueue,
  tokenRequestQueue,
} from "./token-queue";
import {
  getTokenLoadingState,
  subscribeToTokenLoading,
  updateTokenLoadingState,
} from "./token-state";
import type { ClerkGetToken } from "./token-types";

// ============================================================================
// TYPE RE-EXPORTS
// ============================================================================

export type {
  CachedToken,
  ClerkGetToken,
  QueuedRequest,
  TokenLoadingListener,
  TokenLoadingState,
} from "./token-types";

// ============================================================================
// FUNCTION RE-EXPORTS
// ============================================================================

export {
  clearTokenCache,
  getTokenLoadingState,
  stopTokenCacheCleanup,
  subscribeToTokenLoading,
};

// ============================================================================
// MAIN TOKEN GETTER
// ============================================================================

/**
 * Get Clerk JWT for Supabase authentication using native integration
 * Clerk's native Supabase integration automatically adds role: authenticated
 * No custom template needed - uses Clerk's default session token
 *
 * Implements caching and request deduplication to prevent excessive getToken() calls
 */
export async function getSupabaseToken(
  getToken?: ClerkGetToken,
): Promise<string | null> {
  const startTime = Date.now();

  if (!getToken) {
    logger.debug("[getSupabaseToken] No getToken provided", {
      feature: "token-fetch",
    });
    return null;
  }

  try {
    // Check if we're in test mode - if so, return null immediately to avoid hanging
    const isTestBypassActive =
      typeof window !== "undefined" &&
      ((window as { __TEST_BYPASS_ENABLED__?: boolean })
        .__TEST_BYPASS_ENABLED__ ??
        (window as { __TEST_BYPASS_ACTIVE?: boolean }).__TEST_BYPASS_ACTIVE);

    if (isTestBypassActive) {
      logger.debug(
        "[getSupabaseToken] Test bypass active, skipping getToken()",
        {
          feature: "token-fetch",
        },
      );
      return null;
    }

    // Server runtime: never use module-level tokenCache / inFlightRequests (CCP-713).
    // Per-request dedup lives in middleware (requestTokenCache). Vitest sets VITEST=true
    // so unit tests that delete `window` still exercise the browser cache path.
    if (typeof window === "undefined" && process.env.VITEST !== "true") {
      return fetchTokenWithRetry(getToken, "__server_ephemeral__", {
        persistToTokenCache: false,
      });
    }

    const cacheKey = getCacheKey();
    const now = Date.now();

    // Check cache first
    const cached = tokenCache.get(cacheKey);

    // OPTIMIZATION: Use stale token as fallback if available (graceful degradation)
    // This prevents auth failures when token fetch times out but we have a recently expired token
    if (cached) {
      const tokenAge = now - cached.expiresAt;
      const isStaleButUsable =
        tokenAge > 0 && tokenAge < STALE_TOKEN_GRACE_PERIOD_MS;

      if (cached.expiresAt > now) {
        // Token is valid - use it
        // SOLUTION 3: Earlier Token Prefetching
        const timeUntilExpiry = cached.expiresAt - now;

        // CRITICAL FIX: Check inFlightRequests to prevent duplicate prefetches
        const existingPrefetch = inFlightRequests.get(cacheKey);
        if (
          timeUntilExpiry < PREFETCH_THRESHOLD_MS &&
          !cached.refreshPromise &&
          !existingPrefetch &&
          getToken
        ) {
          // Token expires soon - prefetch in background using retry logic
          logger.debug("[getSupabaseToken] Starting early token prefetch", {
            feature: "token-fetch",
            cacheKey,
            timeUntilExpiry,
            prefetchThreshold: PREFETCH_THRESHOLD_MS,
          });

          // Start prefetch in background using fetchTokenWithRetry for better reliability
          const prefetchPromise = (async (): Promise<string | null> => {
            try {
              const prefetchToken = await fetchTokenWithRetry(
                getToken,
                cacheKey,
              );

              if (prefetchToken) {
                const exp = decodeTokenExpiry(prefetchToken);
                const newExpiresAt =
                  typeof exp === "number" && Number.isFinite(exp)
                    ? exp
                    : Date.now() + FALLBACK_TOKEN_CACHE_TTL_MS;
                tokenCache.set(cacheKey, {
                  token: prefetchToken,
                  expiresAt: newExpiresAt,
                });
                logger.debug(
                  "[getSupabaseToken] Token prefetched successfully",
                  {
                    feature: "token-fetch",
                    cacheKey,
                    newExpiresAt,
                    timeUntilExpiry: newExpiresAt - Date.now(),
                  },
                );
                return prefetchToken;
              } else {
                logger.debug(
                  "[getSupabaseToken] Token prefetch returned null (non-critical, will retry on next access)",
                  {
                    feature: "token-fetch",
                    cacheKey,
                  },
                );
              }
              return null;
            } catch (error) {
              // Prefetch failures are non-critical - we still have a valid token
              logger.debug(
                "[getSupabaseToken] Token prefetch failed (non-critical, token still valid)",
                {
                  feature: "token-fetch",
                  cacheKey,
                  error: error instanceof Error ? error.message : String(error),
                  errorType: error instanceof Error ? error.name : typeof error,
                },
              );
              return null;
            } finally {
              // Clear refresh promise after completion (both caches)
              inFlightRequests.delete(cacheKey);
              const updated = tokenCache.get(cacheKey);
              if (updated) {
                updated.refreshPromise = undefined;
              }
            }
          })();

          // Register in both places for global deduplication
          inFlightRequests.set(cacheKey, prefetchPromise);
          cached.refreshPromise = prefetchPromise;
        }

        logger.debug("[getSupabaseToken] Cache HIT", {
          feature: "token-fetch",
          cacheKey,
        });
        const latency = Date.now() - startTime;
        metricsCollector.record({
          operation: "token:fetch:cache_hit",
          latency,
          errorRate: 0,
          payloadSize: 0,
        });
        return cached.token;
      } else if (isStaleButUsable && getToken) {
        // Token is expired but within grace period - await refresh instead of returning stale token
        // Note: Returning stale tokens causes "JWT expired" errors from Supabase
        logger.debug("[getSupabaseToken] Token expired, awaiting refresh", {
          feature: "token-fetch",
          cacheKey,
          tokenAge: tokenAge,
          gracePeriod: STALE_TOKEN_GRACE_PERIOD_MS,
        });

        // PERFORMANCE: Check inFlightRequests FIRST to deduplicate across all requests
        // Use atomic check-and-set to prevent race conditions
        let existingRefresh = inFlightRequests.get(cacheKey);
        if (existingRefresh) {
          logger.debug(
            "[getSupabaseToken] Reusing existing refresh for expired token",
            {
              feature: "token-fetch",
              cacheKey,
            },
          );
          // Await the existing refresh to get a fresh token
          const refreshedToken = await existingRefresh;
          if (refreshedToken) {
            return refreshedToken;
          }
          // Fall through to create new fetch if refresh returned null
        }

        // PERFORMANCE: Double-check cached.refreshPromise to prevent duplicate creation
        // This handles the case where refreshPromise was set but not yet in inFlightRequests
        if (cached.refreshPromise) {
          const refreshedToken = await cached.refreshPromise;
          if (refreshedToken) {
            return refreshedToken;
          }
        }

        // PERFORMANCE: Atomic check-and-set to prevent race condition
        // Re-check inFlightRequests after checking cached.refreshPromise
        existingRefresh = inFlightRequests.get(cacheKey);
        if (!existingRefresh && !cached.refreshPromise) {
          // PERFORMANCE: Create promise and register IMMEDIATELY (synchronously)
          // This prevents race condition where multiple requests all pass checks
          // and then all create promises before any registers
          const refreshPromise = fetchTokenWithRetry(getToken, cacheKey)
            .then((newToken) => {
              if (newToken) {
                const exp = decodeTokenExpiry(newToken);
                const newExpiresAt =
                  typeof exp === "number" && Number.isFinite(exp)
                    ? exp
                    : Date.now() + FALLBACK_TOKEN_CACHE_TTL_MS;
                tokenCache.set(cacheKey, {
                  token: newToken,
                  expiresAt: newExpiresAt,
                });
                logger.debug(
                  "[getSupabaseToken] Expired token refreshed successfully",
                  {
                    feature: "token-fetch",
                    cacheKey,
                    newExpiresAt,
                  },
                );
              }
              return newToken;
            })
            .finally(() => {
              // Clean up both caches
              inFlightRequests.delete(cacheKey);
              const updated = tokenCache.get(cacheKey);
              if (updated) {
                updated.refreshPromise = undefined;
              }
            });

          // PERFORMANCE: Register IMMEDIATELY after creating promise (before any await)
          // This ensures that subsequent requests will see the in-flight fetch
          inFlightRequests.set(cacheKey, refreshPromise);
          cached.refreshPromise = refreshPromise;

          // PERFORMANCE: Re-check one more time - if another request registered while we were creating,
          // use theirs instead (prevents duplicate fetches)
          const anotherRefresh = inFlightRequests.get(cacheKey);
          if (anotherRefresh !== refreshPromise) {
            // Another request registered a different promise - use theirs
            logger.debug(
              "[getSupabaseToken] Another request registered first, using their refresh",
              {
                feature: "token-fetch",
                cacheKey,
              },
            );
            // Clean up our promise (don't await it)
            refreshPromise.catch((err: unknown) => {
              logger.warn(
                "[getSupabaseToken] abandoned concurrent refresh promise rejected",
                {
                  operation: "SUPABASE_TOKEN_ABANDONED_REFRESH",
                  cacheKey,
                  error: err instanceof Error ? err.message : String(err),
                },
              );
              void captureMessage(
                "[getSupabaseToken] abandoned refresh rejected (non-blocking)",
                {
                  level: "warning",
                  tags: {
                    swallow: "intentional_non_blocking",
                    operation: "SUPABASE_TOKEN_ABANDONED_REFRESH",
                    surface: "token_utils",
                  },
                },
              );
            });
            // Use the other request's promise
            const refreshedToken = await anotherRefresh;
            if (refreshedToken) {
              return refreshedToken;
            }
          }
        }

        // Await the refresh to get a fresh token (don't return stale token that Supabase rejects)
        if (cached.refreshPromise) {
          const refreshedToken = await cached.refreshPromise;
          if (refreshedToken) {
            return refreshedToken;
          }
        }
        // Fall through to create new fetch if refresh failed
      }
    }

    // Check if there's already an in-flight request for this cache key
    const inFlight = inFlightRequests.get(cacheKey);
    if (inFlight) {
      logger.debug("[getSupabaseToken] Deduplicating concurrent request", {
        feature: "token-fetch",
        cacheKey,
      });
      return inFlight;
    }

    // Create new token request with queuing support
    const tokenPromise = new Promise<string | null>((resolve, reject) => {
      // If we're at capacity, queue the request
      if (isAtCapacity()) {
        logger.debug(
          "[getSupabaseToken] Queueing token request (at capacity)",
          {
            feature: "token-fetch",
            cacheKey,
            activeRequests: activeTokenRequests,
            queueLength: tokenRequestQueue.length,
          },
        );
        enqueueRequest({
          resolve,
          reject,
          cacheKey,
          getToken,
        });
        return;
      }

      // Process immediately if we have capacity
      incrementActiveRequests();
      updateTokenLoadingState({
        isLoading: true,
        activeRequests: activeTokenRequests,
      });

      // Store in-flight request for deduplication
      const fetchPromise = fetchTokenWithRetry(getToken, cacheKey)
        .then((token) => {
          resolve(token);
          return token;
        })
        .catch((error) => {
          reject(error);
          throw error;
        })
        .finally(() => {
          decrementActiveRequests();
          inFlightRequests.delete(cacheKey);
          updateTokenLoadingState({
            activeRequests: activeTokenRequests,
            isLoading: activeTokenRequests > 0 || tokenRequestQueue.length > 0,
          });
          // Process next item in queue
          processTokenQueue(fetchTokenWithRetry);
        });

      inFlightRequests.set(cacheKey, fetchPromise);
    });

    return tokenPromise;
  } catch (error) {
    const latency = Date.now() - startTime;
    logger.error("[getSupabaseToken] getToken() ERROR", {
      feature: "token-fetch",
      error: error instanceof Error ? error.message : String(error),
      errorType: error instanceof Error ? error.name : typeof error,
      latency,
    });

    // Record metrics for error
    metricsCollector.record({
      operation: "token:fetch:error:outer",
      latency,
      errorRate: 1,
      payloadSize: 0,
      endpoint: "getSupabaseToken",
    });

    return null;
  }
}
